import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRightIcon } from 'lucide-react';
import { motion } from 'framer-motion';
const MotionLink = motion(Link);
type Variant = 'solid' | 'outline' | 'outline-light' | 'light' | 'text';
type ButtonProps = {
  variant?: Variant;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
  to?: string;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
  fullWidth?: boolean;
  showArrow?: boolean;
  'aria-label'?: string;
};
const VARIANT_CLASS: Record<Variant, string> = {
  solid: 'btn btn--solid',
  outline: 'btn btn--outline',
  'outline-light': 'btn btn--outline-light',
  light: 'btn btn--light',
  text: 'btn btn--text link'
};

/** The pill button used across the site: solid blue, outline, light, or text. */
export function Button({
  variant = 'solid',
  children,
  className = '',
  style,
  onClick,
  to,
  href,
  type = 'button',
  disabled = false,
  loading = false,
  loadingLabel,
  fullWidth = false,
  showArrow = true,
  ...rest
}: ButtonProps) {
  const classes = [VARIANT_CLASS[variant], fullWidth ? 'w-full' : '', className].filter(Boolean).join(' ');
  const label = loading && loadingLabel ? loadingLabel : children;
  const content = <span className="relative inline-flex items-center justify-center" data-id="element-242" data-mp="om94qjfv10cp">
      <motion.span variants={{
      initial: {
        opacity: 1
      },
      hover: {
        opacity: 0
      }
    }} transition={{
      duration: 0
    }} className="flex items-center gap-2" data-id="element-243" data-mp="12mtmrddyas55">
        {label}
        {showArrow && variant !== 'text' && <ArrowUpRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-244" data-mp="17elg1vohrbyz" />}
      </motion.span>
      
      <motion.span className="absolute left-0 top-0 flex items-center gap-2 overflow-hidden whitespace-nowrap text-left" variants={{
      initial: {
        width: 0,
        opacity: 0
      },
      hover: {
        width: '100%',
        opacity: 1
      }
    }} transition={{
      duration: 0.4,
      ease: 'easeInOut'
    }} data-id="element-245" data-mp="1ldth6u47su5t">
        <span className="flex items-center" data-id="element-246" data-mp="1tdzk7p6pj1oq">
          {label}
          <motion.span animate={{
          opacity: [1, 0]
        }} transition={{
          repeat: Infinity,
          duration: 0.8,
          ease: 'linear'
        }} className="inline-block h-[1.2em] w-[2px] bg-current ml-0.5" data-id="element-247" data-mp="1ydvn1l3o4n2p" />
        </span>
        {showArrow && variant !== 'text' && <ArrowUpRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-248" data-mp="278jqr6tlhwpe" />}
      </motion.span>
    </span>;
  const motionProps = {
    initial: 'initial',
    whileHover: disabled || loading ? 'initial' : 'hover'
  };
  if (to) {
    return <MotionLink to={to} className={classes} style={style} onClick={onClick} {...motionProps} {...rest} data-id="element-249" data-mp="2m2etrw72krso">
        {content}
      </MotionLink>;
  }
  if (href) {
    return <motion.a href={href} className={classes} style={style} onClick={onClick} {...motionProps} {...rest} data-id="element-250" data-mp="rox525uxhow2">
        {content}
      </motion.a>;
  }
  return <motion.button type={type} className={classes} style={style} onClick={onClick} disabled={disabled || loading} aria-busy={loading || undefined} {...motionProps} {...rest} data-id="element-251" data-mp="351t25vjsbjna">
      {content}
    </motion.button>;
}